<template>
  <div class="editor-uploader">
    <template v-if="hasMixedItems">
      <BaseCarousel
        v-if="props.isSlider && props.mixedItems.length > 1"
        ref="mixedCarouselRef"
        v-model:selected-index="selectedIndexOfMixed"
        :items="props.mixedItems"
        :disabled="isCropping"
        :with-dots="props.withDots"
      >
        <template #slide="{ item, index }">
          <MediaImage
            v-if="item.mediaType === MediaType.Image"
            :key="`${item.src}-${index}`"
            :src="item.src"
            :readonly="props.readonly"
            :aspect-width="props.aspectWidth"
            :aspect-height="props.aspectHeight"
          />
          <MediaPlayer
            v-else-if="item.mediaType === MediaType.Video && index === selectedIndexOfMixed"
            :key="`${item.src}-${index}`"
            :src="item.src"
            :poster="item.posterSrc"
            :width="props.width"
            :height="props.height"
            :aspect-width="videoAspectWidth"
            :aspect-height="videoAspectHeight"
            :match-container-aspect="props.matchVideoAspectWithContainer"
          />
          <div
            v-else-if="item.mediaType === MediaType.Video"
            class="editor-uploader__video-poster"
          >
            <BaseAspectRatio :width="videoAspectWidth" :height="videoAspectHeight">
              <BaseImage
                :src="item.posterSrc || item.src"
                object-fit="cover"
                class="editor-uploader__video-poster-image"
              />
            </BaseAspectRatio>
          </div>
        </template>
      </BaseCarousel>
      <template v-else>
        <template
          v-for="(item, index) in props.mixedItems"
          :key="`${item.src}-${index}`"
        >
          <MediaImage
            v-if="item.mediaType === MediaType.Image"
            :src="item.src"
            :readonly="props.readonly"
            :aspect-width="props.aspectWidth"
            :aspect-height="props.aspectHeight"
          />
          <MediaPlayer
            v-else-if="item.mediaType === MediaType.Video"
            :src="item.src"
            :poster="item.posterSrc"
            :width="props.width"
            :height="props.height"
            :aspect-width="videoAspectWidth"
            :aspect-height="videoAspectHeight"
            :match-container-aspect="props.matchVideoAspectWithContainer"
          />
        </template>
      </template>
    </template>
    <template v-else-if="props.mediaType === MediaType.Image">
      <BaseCarousel
        v-if="props.isSlider"
        v-model:selected-index="selectedIndexOfImage"
        :items="props.modelValue"
        :disabled="isCropping"
        :with-dots="props.withDots"
      >
        <template #slide="{ item, index }">
          <MediaImage
            v-if="!props.loadingList?.[index]"
            :key="index"
            v-model:selected-index="selectedIndexOfImage"
            :src="item"
            :src-list="props.modelValue"
            :readonly="props.readonly"
            :aspect-width="props.aspectWidth"
            :aspect-height="props.aspectHeight"
            @delete="handleDeleteMedia(index)"
            @cropped-file="handleCroppedFile($event, index)"
            @update:is-cropping="isCropping = $event"
          />
          <component :is="loadingComponent" v-else />
        </template>
      </BaseCarousel>
      <template v-else>
        <template v-for="(item, index) in props.modelValue" :key="index">
          <MediaImage
            v-if="!props.loadingList?.[index]"
            v-model:selected-index="selectedIndexOfImage"
            :src="item"
            :src-list="props.modelValue"
            :image-index="index"
            :readonly="props.readonly"
            :aspect-width="props.aspectWidth"
            :aspect-height="props.aspectHeight"
            @delete="handleDeleteMedia(index)"
            @cropped-file="handleCroppedFile($event, index)"
          />
          <component :is="loadingComponent" v-else />
        </template>
      </template>
    </template>
    <template v-else-if="props.mediaType === MediaType.Video">
      <BaseCarousel v-if="props.isSlider" :items="props.modelValue" :with-dots="props.withDots">
        <template #slide="{ item, index }">
          <MediaPlayer
            v-if="!props.loadingList?.[index]"
            :src="item"
            :width="props.width"
            :height="props.height"
            :aspect-width="videoAspectWidth"
            :aspect-height="videoAspectHeight"
            :match-container-aspect="props.matchVideoAspectWithContainer"
            :with-close-button="!props.readonly"
            @close="handleDeleteMedia(index)"
          />
          <component :is="loadingComponent" v-else />
        </template>
      </BaseCarousel>
      <template v-else>
        <template v-for="(item, index) in props.modelValue" :key="index">
          <MediaPlayer
            v-if="!props.loadingList?.[index]"
            :src="item"
            :width="props.width"
            :height="props.height"
            :aspect-width="videoAspectWidth"
            :aspect-height="videoAspectHeight"
            :match-container-aspect="props.matchVideoAspectWithContainer"
            :with-close-button="!props.readonly"
            @close="handleDeleteMedia(index)"
          />
          <component :is="loadingComponent" v-else />
        </template>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { MEDIA_ACCEPT, MediaType } from '~/shared/const'
import {
  BaseAspectRatio,
  BaseCarousel,
  BaseImage,
  BaseLoader,
  BaseSkeleton,
  BaseTypography,
} from '~/shared/ui'
import MediaImage from './MediaImage.vue'
import MediaPlayer from './MediaPlayer.vue'

export interface MediaViewMixedItem {
  src: string
  mediaType: MediaType
  posterSrc?: string
}

interface Props {
  modelValue?: string[]
  mixedItems?: MediaViewMixedItem[]
  mediaType?: MediaType
  readonly?: boolean
  mediaAccept?: string
  loading?: boolean
  variant?: 'secondary'
  isSlider?: boolean
  withDots?: boolean
  loadingList?: boolean[]
  aspectWidth?: number
  aspectHeight?: number
  width?: string
  height?: string
  matchVideoAspectWithContainer?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  mediaType: MediaType.Image,
  mediaAccept: MEDIA_ACCEPT,
  variant: 'secondary',
  withDots: true,
  aspectWidth: 1,
  aspectHeight: 1,
  width: '100%',
  height: 'auto',
  matchVideoAspectWithContainer: false,
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'croppedFile', index: number, croppedDataUrl: string): void
  (e: 'deleteItem', index: number): void
  (e: 'close'): void
}>()

const isCropping = ref(false)
const selectedIndexOfImage = ref(0)
const selectedIndexOfMixed = ref(0)
const mixedCarouselRef = ref<InstanceType<typeof BaseCarousel> | null>(null)

const hasMixedItems = computed(() => Boolean(props.mixedItems?.length))

const videoAspectWidth = computed(() => (
  props.matchVideoAspectWithContainer ? props.aspectWidth : 16
))
const videoAspectHeight = computed(() => (
  props.matchVideoAspectWithContainer ? props.aspectHeight : 9
))

watch(
  () => props.mixedItems,
  () => {
    selectedIndexOfMixed.value = 0
  },
  { deep: true },
)

const loadingComponent = h(
  'div',
  { class: 'editor-uploader__loading' },
  {
    default: () => [
      h(
        BaseSkeleton,
        {
          width: '100%',
          height: '300px',
          form: 'rounded',
          class: 'editor-uploader__loading-skeleton',
        },
        {
          default: () => [
            h(BaseLoader, { size: '32px', color: 'var(--color-foreground-caption)' }),
            h(
              BaseTypography,
              {
                type: 'text-mini',
                textAlign: 'center',
                color: 'var(--color-foreground-caption)',
              },
              { default: () => 'Медиа файл обрабатывается ...' },
            ),
          ],
        },
      ),
    ],
  },
)

function handleCroppedFile(croppedDataUrl: string, index: number) {
  const tempMediaUrls = [...props.modelValue]

  tempMediaUrls[index] = croppedDataUrl
  emit('croppedFile', index, croppedDataUrl)
  emit('update:modelValue', tempMediaUrls)
}

function handleDeleteMedia(index: number) {
  const tempMediaUrls = [...props.modelValue]

  tempMediaUrls.splice(index, 1)
  emit('update:modelValue', tempMediaUrls)
  emit('deleteItem', index)

  if (tempMediaUrls.length === 0) {
    emit('close')
  }
}

function restartCarousel() {
  mixedCarouselRef.value?.restartCarousel?.()
}

defineExpose({
  restartCarousel,
})
</script>

<style lang="scss" scoped>
.editor-uploader {
  &:deep(.media-image:not(:last-child)) {
    margin-bottom: rem(8px);
  }

  &:deep(.editor-uploader__loading) {
    height: 100%;
    width: 100%;

    .editor-uploader__loading-skeleton {
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: rem(16px);
      justify-content: center;
      align-items: center;
    }
  }

  &__video-poster,
  &__video-poster-image {
    width: 100%;
    height: 100%;
  }

  &__video-poster-image {
    display: block;

    :deep(.base-image) {
      display: block;
      width: 100%;
      height: 100%;
    }

    :deep(.base-image__img) {
      width: 100%;
      height: 100%;
    }
  }

  &__uploader {
    $length: 6;
    $gap: 10px;

    display: flex;
    flex-direction: column;
    gap: rem(20px);

    &-preview {
      display: flex;
      flex-wrap: wrap;
      gap: rem($gap);

      > * {
        flex: 1 1 100%;
      }

      &-item {
        position: relative;
        border-radius: var(--radius-xs);
        overflow: hidden;
        flex: 0 1 calc(100% / $length - rem(($gap * ($length - 1)) / $length));
      }

      &-remove {
        position: absolute;
        top: rem(6px);
        right: rem(6px);
      }
    }
  }
}
</style>
